import {
    LoadingManager,
    CubeTextureLoader,
    Vector3,
    TextureLoader,
    Texture,
    Mesh,
    Group,
    Bone,
    Box3,
    BoxBufferGeometry,
    BufferGeometry,
    Quaternion,
    Matrix4,
    CubeTexture,
    MeshStandardMaterial,
    sRGBEncoding,
    DoubleSide,
    NearestFilter,
    Scene,
} from "three";
import { GLTFLoader } from "../assets/JS/GLTFLoader";
import { OBB } from "../assets/JS/OBB";
import { AssetType } from "./enums";
import { StaticMesh } from "./StaticMesh";
import * as modelsData from "assets/json/models.json";
import * as rooms from "assets/json/scenes.json";
import { Inventory } from "./InventoryService";
import { Robot } from "./Robot";
import { dataEvents } from "./Events";
import { IDataModel, ISceneConfiguration } from "./interfaces";
import { computeBoundsTree, disposeBoundsTree, acceleratedRaycast } from "three-mesh-bvh";
import { DRACOLoader } from "three/examples/jsm/loaders/DRACOLoader";
import { RiggedMesh } from "./RiggedMesh";
import * as SkeletonUtils from "three/examples/jsm/utils/SkeletonUtils.js";

export interface RiggedInventoryType {
    surgeon: RiggedMesh | null;
}

export const RiggedInventory: RiggedInventoryType = {
    surgeon: null,
};

type RiggedModelItem = {
    name: string;
    mesh: {
        filename: string;
        extention: string;
    };
};

export class LoaderService {
    loadingMan: LoadingManager;
    gltfLoader: GLTFLoader;
    envMapLoader: CubeTextureLoader;
    texLoader: TextureLoader;
    patientBedsMesh: { [id: string]: { patientMesh: StaticMesh; bedMesh: StaticMesh } } = {};
    consoleMesh!: Mesh;
    trocarMesh!: Mesh;
    iBsuMesh!: Mesh;
    vBsuMesh!: Mesh;
    loadedAssets: { [id: string]: { obj: StaticMesh | Robot } } = {};
    _modelData: IDataModel;
    assetsloaded = false;
    /**
     * Handle loading assets
     */
    constructor(private scene: Scene) {
        BufferGeometry.prototype.computeBoundsTree = computeBoundsTree;
        BufferGeometry.prototype.disposeBoundsTree = disposeBoundsTree;
        Mesh.prototype.raycast = acceleratedRaycast;

        this.loadingMan = new LoadingManager(this.onLoad, this.onProgress, this.onError);

        this.gltfLoader = new GLTFLoader(this.loadingMan);

        const dracoLoader = new DRACOLoader();
        dracoLoader.setDecoderPath("/draco/");
        this.gltfLoader.setDRACOLoader(dracoLoader);
        this.envMapLoader = new CubeTextureLoader(this.loadingMan);
        this.texLoader = new TextureLoader();
        this._modelData = modelsData.globalSettings;
        this.loadEnvMap(this._modelData.Paths.envMapFolder);

        Object.keys(this._modelData.settings.BSUColors).forEach((key) => {
            const hexcolor = (this._modelData.settings.BSUColors as { [id: string]: string })[key];
            Inventory.colors[key] = {
                name: key,
                hexColor: hexcolor,
                isUsed: false,
            };
        });
        this.loadAssets();
    }

    loadAssets(): void {
        //load ibsu
        const item = this._modelData.models.iBSU;
        let url = this.getItemUrl(item.mesh.filename, item.mesh);
        // eslint-disable-next-line  @typescript-eslint/no-explicit-any
        this.loadModel(url, (gltf: any) => {
            const bsuProp = this.processBSUModel(gltf.scene as Group, item);
            if (bsuProp) {
                Inventory.iBsu = new Robot(bsuProp.arm, bsuProp.effector, bsuProp.root);
                Inventory.iBsu.setBSUMesh(bsuProp.baseMesh, bsuProp.armMesh);
                Inventory.iBsu.group = bsuProp.group;
                Inventory.iBsu.setRotationY(90, undefined);
                Inventory.iBsu.name = item.name;
            }
        });

        //load vbsu
        const item1 = this._modelData.models.vBSU;
        const url1 = this.getItemUrl(item1.mesh.filename, item1.mesh);
        // eslint-disable-next-line  @typescript-eslint/no-explicit-any
        this.loadModel(url1, (gltf: any) => {
            const bsuProp = this.processBSUModel(gltf.scene as Group, item1);
            if (bsuProp) {
                Inventory.vBsu = new Robot(bsuProp.arm, bsuProp.effector, bsuProp.root);
                Inventory.vBsu.setBSUMesh(bsuProp.baseMesh, bsuProp.armMesh);
                Inventory.vBsu.group = bsuProp.group;
                Inventory.vBsu.name = item1.name;
                Inventory.vBsu.setRotationY(90, undefined);
                Inventory.vBsu.setLight(Inventory.colors["white"]);
            }
        });

        //load console
        const consol = this._modelData.models.console;
        url = this.getItemUrl(consol.mesh.filename, consol.mesh);
        // eslint-disable-next-line  @typescript-eslint/no-explicit-any
        this.loadModel(url, (gltf: any) => {
            const meshes: Mesh[] = [];
            const scale = new Vector3(1.0, 1.0, 1.0);
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            gltf.scene.traverse((_child: any) => {
                if (_child instanceof Mesh) {
                    _child.castShadow = true;
                    _child.receiveShadow = true;
                    _child.frustumCulled = false;
                    meshes.push(_child);
                    if (_child.parent) {
                        scale.copy(_child.parent.scale);
                    }
                }
            });

            Inventory.SM_console = new StaticMesh(AssetType.Console, 0.0);
            meshes.forEach((_child) => {
                const mat = _child.material as MeshStandardMaterial;
                if (mat.map) {
                    mat.map.encoding = sRGBEncoding;
                    mat.map.anisotropy = 4;
                }
                Inventory.SM_console.addMesh(_child);
            });
            Inventory.SM_console.name = consol.name;
            Inventory.SM_console.group.scale.copy(scale);

            Inventory.SM_console.addCollisionEnvelop(
                this.computeCollision(Inventory.SM_console.group)
            );
        });

        this.loadFlattenedStaticModel(
            this._modelData.models.trolley,
            AssetType.Trolley,
            (mesh) => {
                Inventory.SM_trolley = mesh;
            },
            0.02
        );

        this.loadFlattenedStaticModel(
            this._modelData.models.surgeon,
            AssetType.Surgeon,
            (mesh) => {
                Inventory.SM_surgeon = mesh;
            },
            0.02
        );

        // this.loadRiggedStaticModel(
        //     this._modelData.models.surgeon,
        //     AssetType.Surgeon,
        //     (mesh) => {
        //         Inventory.SM_surgeon = mesh;
        //     },
        //     0.02
        // );

        this.loadFlattenedStaticModel(
            this._modelData.models.slave,
            AssetType.Slave,
            (mesh) => {
                Inventory.SM_slave = mesh;
            },
            0.02
        );

        this.loadFlattenedStaticModel(
            this._modelData.models.anesthesia,
            AssetType.Anesthesia,
            (mesh) => {
                Inventory.SM_anesthesia = mesh;
            },
            0.02
        );

        //load stack
        const stack = this._modelData.models.stack;
        url = this.getItemUrl(stack.mesh.filename, stack.mesh);
        // eslint-disable-next-line  @typescript-eslint/no-explicit-any
        this.loadModel(url, (gltf: any) => {
            const meshes: Mesh[] = [];
            const scale = new Vector3(1.0, 1.0, 1.0);
            // eslint-disable-next-line  @typescript-eslint/no-explicit-any
            gltf.scene.traverse((_child: any) => {
                if (_child instanceof Mesh) {
                    _child.castShadow = true;
                    const mat = _child.material as MeshStandardMaterial;
                    this.loadEnvMap(this._modelData.Paths.envMapFolder, (texture) => {
                        mat.envMap = texture;
                        mat.envMap.needsUpdate = true;
                    });

                    if (mat.map) mat.map.anisotropy = 4;
                    meshes.push(_child);
                    if (_child.parent) {
                        scale.copy(_child.parent.scale);
                    }
                }
            });
            Inventory.SM_stack = new StaticMesh(AssetType.Stack, 0.02);
            meshes.forEach((_child) => {
                Inventory.SM_stack.addMesh(_child);
            });
            Inventory.SM_stack.name = stack.name;
            Inventory.SM_stack.group.scale.copy(scale);
            Inventory.SM_stack.addCollisionEnvelop(this.computeCollision(Inventory.SM_stack.group));
        });

        //load trocar
        const trocar = this._modelData.models.trocar;
        url = this.getItemUrl(trocar.mesh.filename, trocar.mesh);
        // eslint-disable-next-line  @typescript-eslint/no-explicit-any
        this.loadModel(url, (gltf: any) => {
            const meshes: Mesh[] = [];
            const scale = new Vector3(1.0, 1.0, 1.0);
            // eslint-disable-next-line  @typescript-eslint/no-explicit-any
            gltf.scene.traverse((_child: any) => {
                _child.castShadow = true;
                if (_child instanceof Mesh) {
                    meshes.push(_child);
                    if (_child.parent) {
                        scale.copy(_child.parent.scale);
                    }
                }
            });

            Inventory.SM_trocar = new StaticMesh(AssetType.Trocar);
            meshes.forEach((_child) => {
                Inventory.M_trocar = _child.material as MeshStandardMaterial;
                if (Inventory.M_trocar.map) Inventory.M_trocar.map.anisotropy = 8;
                this.loadEnvMap(this._modelData.Paths.envMapFolder, (tex) => {
                    Inventory.M_trocar.envMap = tex;
                    Inventory.M_trocar.envMap.needsUpdate = true;
                    Inventory.M_trocar.envMapIntensity = 3.0;
                });

                Inventory.SM_trocar.addMesh(_child);
            });
            Inventory.SM_trocar.group.scale.copy(scale);
            //Inventory.SM_trocar.group.rotateX(Math.PI / 2);
            Inventory.SM_trocar.group.updateMatrixWorld(true);
            Inventory.SM_trocar.name = trocar.name;
        });

        //load patient
        this._modelData.models.patientBed.forEach((item) => {
            const fileName = item.mesh.filename;
            const url = [
                this._modelData.Paths.ModelPath,
                fileName,
                fileName + item.mesh.extention,
            ].join("/");
            const id = item.name;
            Inventory.SM_PatientBed[id] = {
                SM_Patient: new StaticMesh(AssetType.Patient),
                SM_Bed: new StaticMesh(AssetType.Bed),
            };
            // eslint-disable-next-line  @typescript-eslint/no-explicit-any
            this.loadModel(url, (gltf: any) => {
                const meshes: Mesh[] = [];

                const patientName = item.mesh.patient;
                const bedName = item.mesh.bed;
                const zoneMeshName = item.mesh.zone;
                const dropzoneMeshName = item.mesh.dropzone;
                let scale = new Vector3(1.0, 1.0, 1.0);
                // eslint-disable-next-line  @typescript-eslint/no-explicit-any
                gltf.scene.traverse((_child: any) => {
                    if (_child instanceof Mesh) {
                        _child.castShadow = true;
                        meshes.push(_child);
                    }
                });

                if (meshes.length > 0) {
                    for (const _child of meshes) {
                        if (_child.name.indexOf(patientName) > -1) {
                            // patient

                            const q = new Quaternion();
                            if (_child.parent) {
                                scale.copy(_child.parent.scale);
                                _child.parent.updateMatrixWorld(true);
                                q.copy(_child.parent.quaternion);
                                //_child.scale.multiply(_child.parent.scale);
                                _child.geometry.computeBoundsTree();
                            }
                            const M_Patient = _child.material as MeshStandardMaterial;
                            M_Patient.side = DoubleSide;
                            if (M_Patient.map) {
                                M_Patient.map.anisotropy = 8;
                                M_Patient.needsUpdate = true;
                            }
                            /* _child.userData["type"] = AssetType.Patient; */
                            //_child.scale.copy(scale);
                            _child.updateMatrixWorld(true);
                            Inventory.SM_PatientBed[id].SM_Patient.addMesh(_child);
                            _child.userData["patientBed"] = Inventory.SM_PatientBed[id];
                            if (item.collision.patient.filename.length > 0) {
                                const patientCollisionUrl = this.getItemUrl(
                                    item.mesh.filename,
                                    item.collision.patient
                                );
                                this.loadModel(patientCollisionUrl, (gltf) => {
                                    const collisionEnvelop: { obb: Mesh[]; sphere: Mesh[] } = {
                                        obb: [],
                                        sphere: [],
                                    };
                                    // eslint-disable-next-line  @typescript-eslint/no-explicit-any
                                    gltf.scene.traverse((_child: any) => {
                                        if (_child instanceof Mesh) {
                                            this.processCollisionMesh(_child, collisionEnvelop);
                                        }
                                    });
                                    if (item.collision.patient.scale)
                                        Inventory.SM_PatientBed[
                                            id
                                        ].SM_Patient.collisiongroup.scale.fromArray(
                                            item.collision.patient.scale
                                        );
                                    Inventory.SM_PatientBed[id].SM_Patient.setCollisionEnvolop(
                                        collisionEnvelop
                                    );
                                });
                            }

                            Inventory.SM_PatientBed[id].SM_Patient.group.scale.copy(scale);
                            Inventory.SM_PatientBed[id].SM_Patient.group.quaternion.multiply(q);
                            Inventory.SM_PatientBed[id].SM_Patient.name = item.name;
                        } else {
                            if (_child.name.indexOf(bedName) > -1) {
                                // bed
                                const q = new Quaternion();
                                scale = new Vector3(1.0, 1.0, 1.0);
                                if (_child.parent) {
                                    q.copy(_child.parent.quaternion);
                                    scale.copy(_child.parent.scale);
                                }
                                _child.geometry.computeBoundsTree();
                                /* _child.userData["type"] = AssetType.Bed; */

                                Inventory.SM_PatientBed[id].SM_Bed.addMesh(_child);
                                _child.userData["patientBed"] = Inventory.SM_PatientBed[id];
                                Inventory.SM_PatientBed[id].SM_Bed.group.scale.copy(scale);
                                Inventory.SM_PatientBed[id].SM_Bed.group.quaternion.multiply(q);
                                if (item.collision.bed.filename.length > 0) {
                                    const patientCollisionUrl = this.getItemUrl(
                                        item.mesh.filename,
                                        item.collision.bed
                                    );
                                    this.loadModel(patientCollisionUrl, (gltf) => {
                                        const collisionEnvelop: { obb: Mesh[]; sphere: Mesh[] } = {
                                            obb: [],
                                            sphere: [],
                                        };
                                        // eslint-disable-next-line  @typescript-eslint/no-explicit-any
                                        gltf.scene.traverse((_child: any) => {
                                            if (_child instanceof Mesh) {
                                                this.processCollisionMesh(_child, collisionEnvelop);
                                            }
                                        });
                                        Inventory.SM_PatientBed[id].SM_Bed.setCollisionEnvolop(
                                            collisionEnvelop
                                        );
                                        if (item.collision.bed.scale)
                                            Inventory.SM_PatientBed[
                                                id
                                            ].SM_Bed.collisiongroup.scale.fromArray(
                                                item.collision.bed.scale
                                            );
                                    });
                                }
                                Inventory.SM_PatientBed[id].SM_Bed.name = item.name;
                            } else {
                                if (zoneMeshName && _child.name.indexOf(zoneMeshName) > -1) {
                                    _child.material = new MeshStandardMaterial();
                                    _child.visible = false;
                                    Inventory.SM_PatientBed[id].SM_Patient.group.userData["zone"] =
                                        _child;
                                    Inventory.SM_PatientBed[id].SM_Patient.group.add(_child);
                                } else if (
                                    dropzoneMeshName &&
                                    _child.name.indexOf(dropzoneMeshName) > -1
                                ) {
                                    _child.material = new MeshStandardMaterial({
                                        color: "#19ddf7",
                                        opacity: 0.3,
                                        transparent: true,
                                        visible: true,
                                    });
                                    _child.visible = false;
                                    Inventory.SM_PatientBed[id].SM_Patient.group.userData[
                                        "dropzone"
                                    ] = _child;
                                    Inventory.SM_PatientBed[id].SM_Patient.group.add(_child);
                                }
                            }
                        }
                    }
                }
            });

            if (item.ports && item.ports.filename.length > 0) {
                const portsUrl = this.getItemUrl(item.mesh.filename, item.ports);
                this.loadModel(portsUrl, (gltf) => {
                    const portTranform: { [id: string]: { position: Vector3; matrix: Matrix4 } } =
                        {};
                    if (item.ports?.scale) gltf.scene.scale.fromArray(item.ports.scale);
                    // eslint-disable-next-line  @typescript-eslint/no-explicit-any
                    gltf.scene.traverse((_child: any) => {
                        if (_child instanceof Mesh) {
                            portTranform[_child.name] = this.processPorts(_child);
                        }
                    });
                    Inventory.Ports[item.name] = portTranform;
                });
            }
        });
    }
    /**
     * Load a GLTF model
     * @param url Path to the .gltf file
     * @param onLoaded executed when the file loaded
     */
    // eslint-disable-next-line  @typescript-eslint/no-explicit-any
    public loadModel(url: string, onLoaded: (gltf: any) => void): void {
        this.gltfLoader?.load(url, onLoaded, null, null);
    }

    /**
     * Load a cube texture used as an environment map
     * @param path path to the folder containing the textures
     * @param ext type of the files (png, jpg, etc)
     * @returns
     */
    public loadEnvMap(
        path: string,
        onLoaded: (texture: CubeTexture) => void = () => {
            return;
        },
        ext = "jpg"
    ): CubeTexture {
        this.envMapLoader.setPath(path);
        const envMap = this.envMapLoader.load(
            ["px." + ext, "nx." + ext, "py." + ext, "ny." + ext, "pz." + ext, "nz." + ext],
            (texture) => {
                Inventory.T_envMap = texture;
                Inventory.T_envMap.needsUpdate = true;
                onLoaded(texture);
            }
        );
        return envMap;
    }
    public loadTexture(
        url: string,
        onReady: (tex: Texture) => void = function () {
            return;
        }
    ): Texture {
        return this.texLoader.load(url, onReady);
    }
    private onLoad(): void {
        dataEvents.emit("assetsLoaded");
    }

    private onProgress(url: string, loaded: number, total: number): void {
        dataEvents.emit("loading", loaded, total);
    }

    private onError(url: string): void {
        console.error("error loading : ", url);
        dataEvents.emit("errorLoading", url);
    }
    private getItemUrl(foldername: string, item: { filename: string; extention: string }): string {
        return [this._modelData.Paths.ModelPath, foldername, item.filename + item.extention].join(
            "/"
        );
    }
    /**
     * Load a scene configuration from a json file
     * @param url path to the json file
     * @returns
     */

    static loadSceneConfiguration(key: string): ISceneConfiguration {
        // load scene configuration
        const sceneConf = rooms.preBuiltScenes as { [key: string]: ISceneConfiguration };
        return sceneConf[key];
    }
    computeCollision(group: Group): Mesh {
        const box = new Box3().setFromObject(group);

        const size = new Vector3();
        const center = new Vector3();

        box.getSize(size);
        box.getCenter(center);
        //const obb1 = new OBB().halfSize.copy(size).multiplyScalar(0.5);
        const meshCollision = new Mesh(new BoxBufferGeometry(size.x, size.y, size.z));
        meshCollision.position.copy(center);
        meshCollision.updateMatrix();
        meshCollision.geometry.userData.obb = new OBB();
        meshCollision.geometry.userData.obb.halfSize.copy(size).multiplyScalar(0.5);
        meshCollision.userData = { obb: new OBB() };
        /* meshCollision.userData.obb.copy(meshCollision.geometry.userData.obb);
        meshCollision.userData.obb.applyMatrix4(meshCollision.matrixWorld); */
        return meshCollision;
    }

    processBSUModel(
        gltf: Group,
        item: { mesh: { meshName: { base: string; arm: string } } }
    ):
        | {
              arm: Bone[];
              effector: Bone;
              root: Bone;
              baseMesh: Mesh;
              armMesh: Mesh;
              group: Group;
          }
        | undefined {
        const bones: Bone[] = [];
        const arm: Bone[] = [];
        const group: Group = gltf;
        const effector: Bone = new Bone();
        const meshes: { [id: string]: Mesh } = {};
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        gltf.traverse((_child: any) => {
            if (_child instanceof Mesh) {
                _child.frustumCulled = false;
                _child.castShadow = false;
                const mat = _child.material as MeshStandardMaterial;
                if (mat.map) {
                    mat.map.anisotropy = 16;
                    mat.map.magFilter = NearestFilter;
                    mat.map.minFilter = NearestFilter;
                    mat.needsUpdate = true;
                }
                if (
                    _child.name === item.mesh.meshName.base ||
                    _child.name === item.mesh.meshName.arm
                ) {
                    meshes[_child.name] = _child;
                }
            }
            if (_child instanceof Bone) {
                bones.push(_child);
                /* if (_child.name === "root") {
                    root = _child;
                } */
            }
        });
        if (bones.length > 0) {
            arm.push(bones[3]);
            for (let i = 3; i < 12; i++) {
                arm.push(bones[i]);
            }
            const n = arm.length;
            arm[2].userData["jointLimits"] = {
                qmin: -0.47 * Math.PI,
                qmax: 0.47 * Math.PI,
            };
            arm[4].userData["jointLimits"] = { qmin: 0.0, qmax: 0.95 * Math.PI };
            arm[n - 4].userData["jointLimits"] = { qmin: 0, qmax: Math.PI };
            arm[n - 3].userData["jointLimits"] = { qmin: -Math.PI, qmax: 0.0 };
            arm[n - 1].add(effector);
            effector.matrixWorld = arm[n - 1].matrixWorld.clone();
            effector.translateZ(0.25);
            effector.translateY(0.012);
            effector.updateMatrixWorld();
            const res = {
                arm: arm,
                effector: effector,
                root: bones[0],
                baseMesh: meshes[item.mesh.meshName.base],
                armMesh: meshes[item.mesh.meshName.arm],
                group: group,
            };

            return res;
        }
        return;
    }

    processCollisionMesh(_child: Mesh, collisionEnvelop: { obb: Mesh[]; sphere: Mesh[] }): void {
        if (_child.name.startsWith("CSM")) {
            const geom = _child.geometry as BufferGeometry;
            geom.computeBoundingSphere();
            collisionEnvelop.sphere.push(_child);
        } else if (_child.name.startsWith("CBM")) {
            // bed
            //_child.updateMatrix();
            const rotM = _child.matrixWorld.clone();
            const q = new Quaternion();
            _child.matrixWorld.decompose(new Vector3(), q, new Vector3());
            const iq = q.clone().invert();
            _child.applyQuaternion(iq);
            const geom = _child.geometry as BufferGeometry;
            geom.computeBoundingBox();
            const size = new Vector3();
            if (geom.boundingBox != null) {
                geom.boundingBox.getSize(size);
                _child.geometry.userData["obb"] = new OBB();
                _child.geometry.userData.obb.halfSize.copy(size).multiplyScalar(0.5);
                _child.applyMatrix4(rotM);
                _child.userData["obb"] = new OBB();
            }
            collisionEnvelop.obb.push(_child);
        }
    }

    processPorts(child: Mesh): { position: Vector3; matrix: Matrix4 } {
        child.updateMatrix();
        child.updateMatrixWorld();
        return { position: child.position, matrix: child.matrixWorld };
    }

    private loadRiggedStaticModel(
        item: {
            name: string;
            mesh: {
                filename: string;
                extention: string;
            };
        },
        assetType: AssetType,
        targetSetter: (mesh: StaticMesh) => void,
        collisionOffset = 0.02
    ): void {
        const url = this.getItemUrl(item.mesh.filename, item.mesh);

        this.loadModel(url, (gltf: any) => {
            const root = gltf.scene as Group;

            if (!root) {
                console.error("Rigged model failed to load:", item.name);
                return;
            }

            root.updateMatrixWorld(true);

            const staticMesh = new StaticMesh(assetType, collisionOffset);
            staticMesh.name = item.name;
            staticMesh.meshes = [];

            root.traverse((child: any) => {
                if (child.isMesh || child.isSkinnedMesh) {
                    child.visible = true;
                    child.castShadow = true;
                    child.receiveShadow = true;
                    child.frustumCulled = false;
                    child.userData = { type: assetType, staticmesh: staticMesh };

                    if (Array.isArray(child.material)) {
                        child.material.forEach((mat: MeshStandardMaterial) => {
                            if (mat.map) {
                                mat.map.encoding = sRGBEncoding;
                                mat.map.anisotropy = 4;
                            }
                            mat.needsUpdate = true;
                        });
                    } else if (child.material) {
                        const mat = child.material as MeshStandardMaterial;
                        if (mat.map) {
                            mat.map.encoding = sRGBEncoding;
                            mat.map.anisotropy = 4;
                        }
                        mat.needsUpdate = true;
                    }

                    staticMesh.meshes.push(child as Mesh);
                }
            });

            root.updateMatrixWorld(true);

            const box = new Box3().setFromObject(root);
            const center = new Vector3();
            const size = new Vector3();
            box.getCenter(center);
            box.getSize(size);

            root.position.sub(center);
            root.position.y += size.y / 2;

            const maxDim = Math.max(size.x, size.y, size.z);
            const scaleFix = maxDim > 0 ? 2 / maxDim : 1;
            root.scale.setScalar(scaleFix);

            // Keep the rig/skeleton as a child. StaticMesh.setPosition() moves this wrapper,
            // so it will not overwrite the rig's local centering/scale fix.
            staticMesh.group.add(root);
            staticMesh.group.updateMatrixWorld(true);

            staticMesh.addCollisionEnvelop(this.computeCollision(staticMesh.group));

            targetSetter(staticMesh);
        });
    }

    private loadFlattenedStaticModel(
        item: {
            name: string;
            mesh: {
                filename: string;
                extention: string;
            };
        },
        assetType: AssetType,
        targetSetter: (mesh: StaticMesh) => void,
        collisionOffset = 0.02
    ): void {
        const url = this.getItemUrl(item.mesh.filename, item.mesh);

        this.loadModel(url, (gltf: any) => {
            const root = gltf.scene as Group;

            root.updateMatrixWorld(true);

            const staticMesh = new StaticMesh(assetType, collisionOffset);

            const fixMaterial = (material: MeshStandardMaterial) => {
                if (Inventory.T_envMap) {
                    material.envMap = Inventory.T_envMap;
                    material.envMapIntensity = 1.5;
                }

                if (material.map) {
                    material.map.encoding = sRGBEncoding;
                    material.map.anisotropy = 4;
                }

                material.needsUpdate = true;
            };

            root.traverse((_child: any) => {
                if (_child instanceof Mesh) {
                    const mesh = _child.clone();

                    mesh.geometry = _child.geometry.clone();
                    mesh.geometry.applyMatrix4(_child.matrixWorld);

                    mesh.position.set(0, 0, 0);
                    mesh.rotation.set(0, 0, 0);
                    mesh.quaternion.identity();
                    mesh.scale.set(1, 1, 1);
                    mesh.updateMatrixWorld(true);

                    mesh.castShadow = true;
                    mesh.receiveShadow = true;
                    mesh.frustumCulled = false;

                    if (Array.isArray(mesh.material)) {
                        mesh.material.forEach((m) => fixMaterial(m as MeshStandardMaterial));
                    } else {
                        fixMaterial(mesh.material as MeshStandardMaterial);
                    }

                    staticMesh.addMesh(mesh);
                }
            });

            staticMesh.name = item.name;
            staticMesh.group.updateMatrixWorld(true);

            const box = new Box3().setFromObject(staticMesh.group);
            const yOffset = -box.min.y;

            staticMesh.group.traverse((_child: any) => {
                if (_child instanceof Mesh) {
                    _child.geometry.translate(0, yOffset, 0);
                    _child.geometry.computeBoundingBox();
                    _child.geometry.computeBoundingSphere();
                }
            });

            staticMesh.group.updateMatrixWorld(true);

            staticMesh.addCollisionEnvelop(this.computeCollision(staticMesh.group));

            targetSetter(staticMesh);
        });
    }

    public loadRiggedModel(
        item: { name: string; mesh: { filename: string; extention: string } },
        onLoaded: (rigged: RiggedMesh) => void
    ): void {
        const url = this.getItemUrl(item.mesh.filename, item.mesh);

        this.loadModel(url, (gltf: any) => {
            const group = gltf.scene as Group;

            if (!group) {
                console.error("Rigged model failed to load:", item.name);
                return;
            }

            const bones: Bone[] = [];

            group.traverse((child: any) => {
                if (child.isSkinnedMesh && child.skeleton) {
                    bones.push(...child.skeleton.bones);
                }

                if (child.isMesh || child.isSkinnedMesh) {
                    child.visible = true;
                    child.castShadow = true;
                    child.receiveShadow = true;
                    child.frustumCulled = false;

                    if (Array.isArray(child.material)) {
                        child.material.forEach((mat: MeshStandardMaterial) => {
                            mat.needsUpdate = true;
                        });
                    } else if (child.material) {
                        (child.material as MeshStandardMaterial).needsUpdate = true;
                    }
                }
            });

            group.updateMatrixWorld(true);

            const box = new Box3().setFromObject(group);
            const center = new Vector3();
            const size = new Vector3();

            box.getCenter(center);
            box.getSize(size);

            // Move the model so its centre is around the origin.
            group.position.sub(center);

            // Put the feet/base on y = 0.
            group.position.y += size.y / 2;

            // Normalise the huge imported scale to something sane.
            const maxDim = Math.max(size.x, size.y, size.z);
            const scaleFix = maxDim > 0 ? 2 / maxDim : 1;
            group.scale.setScalar(scaleFix);

            group.updateMatrixWorld(true);

            console.log("Rigged loaded:", item.name, {
                bones: bones.length,
                hasScene: !!group,
                size,
                scaleFix,
            });

            // Important: do NOT add this source group to the scene here.
            // Keep it as the template, then clone it with SkeletonUtils on click.
            onLoaded(new RiggedMesh(group, bones));
        });
    }

    public addRiggedModelToScene(
        rigged: RiggedMesh | null,
        position: Vector3 = new Vector3(0, 0, 0),
        rotationY = 0
    ): Group | null {
        if (!rigged) {
            console.warn("Tried to add rigged model before it was loaded");
            return null;
        }

        const clone = (SkeletonUtils as any).clone(rigged.group) as Group;

        clone.position.copy(position);
        clone.rotation.y = rotationY;
        clone.visible = true;

        clone.traverse((child: any) => {
            if (child.isMesh || child.isSkinnedMesh) {
                child.visible = true;
                child.castShadow = true;
                child.receiveShadow = true;
                child.frustumCulled = false;
            }
        });

        clone.updateMatrixWorld(true);
        this.scene.add(clone);

        console.log("Rigged model clone added to scene:", clone);

        return clone;
    }

    public addSurgeonToScene(
        position: Vector3 = new Vector3(0, 0, 0),
        rotationY = 0
    ): Group | null {
        return this.addRiggedModelToScene(RiggedInventory.surgeon, position, rotationY);
    }
}
